import React, { useCallback, type JSX } from 'react';
import {
    ClickAwayListener,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { UserMenu } from '@brightlayer-ui/react-components';
import Avatar from '@mui/material/Avatar';
import Settings from '@mui/icons-material/Settings';
import Input from '@mui/icons-material/Input';
import Group from '@mui/icons-material/Group';
import AccountTree from '@mui/icons-material/AccountTree';
import SmartToy from '@mui/icons-material/SmartToy';
import Dashboard from '@mui/icons-material/Dashboard';
import TableChart from '@mui/icons-material/TableChart';
import NotificationsPaused from '@mui/icons-material/NotificationsPaused';
import Snooze from '@mui/icons-material/Snooze';
import Help from '@mui/icons-material/Help';
import Policy from '@mui/icons-material/Policy';
import Person from '@mui/icons-material/Person';

import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import makeStyles from '@mui/styles/makeStyles';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { getUserInitials } from '../utils/helpers';
import { useOktaAuth } from '@okta/okta-react';
import { Theme, useTheme } from '@mui/material/styles';

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CustomTransComponent, NeedHelpScreen, PrivacyPolicy } from './';
import { useGetUserLogoutApiCallMutation, useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { useGetRegisteredUserAgreementsPublicQuery } from '@fiji/common/src/features/userManagement/userApi';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import { Profile, FeatureFlagData } from '@fiji/common/src/types';
import { DeviceConfiguration, PushNotificationSolid } from '@brightlayer-ui/icons-mui';
import * as Colors from '@brightlayer-ui/colors';
import { useRBAC } from '../hooks';
import { selectCurrentPermission, selectGlobalPermissions } from '@fiji/common/src/features/profile/profileSlice';
import { useCountdownTimer } from 'hooks/useCountdownTimer';
import CreateSnoozeModal from './CreateSnoozeModal';
import { MonetizationOn } from '@mui/icons-material';
import { Quarantine } from 'pages/Quarantine';
import { useGetAllFeatureFlagQuery } from '@fiji/common/src/features/featureFlag/featureFlagApi';
import { TOS_PRIVACY_FEATURE_FLAG } from '@fiji/common/src/constants';

const useStyles = makeStyles((theme: Theme) => ({
    toolbarRightItem: {
        marginRight: theme.spacing(2),
        marginLeft: '11px',
    },
    headerMenuListItem: {
        '& .BluiInfoListItem-root': {
            padding: '9px 16px',
        },
    },
}));

type HeaderActionsProps = {
    disabled?: boolean;
    isSystemAdmin?: boolean;
    isUpcomingServicePortal?: boolean;
};

/* The above code is defining a React functional component called `HeaderActions`. It is exporting this
component as a named export. The component is wrapped in the `React.memo` function, which is used
for performance optimization by memoizing the component and preventing unnecessary re-renders. */

export const HeaderActions: React.FC<HeaderActionsProps> = React.memo(
    ({ disabled, isSystemAdmin, isUpcomingServicePortal }): JSX.Element => {
        const theme: any = useTheme();
        const { t } = useTranslation();
        const navigate = useNavigate();
        const classes = useStyles(theme);
        const { oktaAuth } = useOktaAuth();
        const policyRef = React.useRef<any>(null);
        const currentOrg = useTypedSelector(selectedOrg);
        const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
        const isSuperRealm = useTypedSelector((state) => state.common.isSuperRealm);
        const isAdminRealm = useTypedSelector((state) => state.common.isAdminRealm);
        const timerString = useTypedSelector((state) => state.alarmtimeline.timerString);
        const permissions = useTypedSelector(selectCurrentPermission);
        const globalPermissions = useTypedSelector(selectGlobalPermissions);
        const { hasPermission } = useRBAC(globalPermissions?.length ? globalPermissions : permissions);
        const canViewGroups = hasPermission('group-management_view-group-management');
        const canViewGroupsExpired = hasPermission('group-management_view-group-management_hasExpired');
        const canViewSystemDashboards = hasPermission('dashboard-management_view-system-dashboards');
        const canViewSystemDashboardsExpired = hasPermission('dashboard-management_view-system-dashboards_hasExpired');
        const canViewUserDashboards = hasPermission('dashboard-management_view-user-dashboards');
        const canViewUserDashboardsExpired = hasPermission('dashboard-management_view-user-dashboards_hasExpired');
        const canViewSystemWidgets = hasPermission('map-summary_view-system-widgets');
        const canViewSystemWidgetsExpired = hasPermission('map-summary_view-system-widgets_hasExpired');
        const canViewUserWidgets = hasPermission('widget-management_view-user-widgets');
        const canViewUserWidgetsExpired = hasPermission('widget-management_view-user-widgets_hasExpired');
        const canEditDevices = hasPermission('edit-devices');
        const canViewUser = hasPermission('user-management_view-users');
        const canViewUserExpired = hasPermission('user-management_view-users_hasExpired');
        const canViewRole = hasPermission('role-management_view-roles');
        const canViewRoleExpired = hasPermission('role-management_view-roles_hasExpired');
        const canCreateAutomation = hasPermission('automation-management_create-automations');
        const canCreateUser = hasPermission('user-management_create-users');
        const canCreateGroup = hasPermission('group-management_create-groups');
        const canViewLogs = hasPermission('accounting-audit-log_view-logs');
        const canViewLogsExpired = hasPermission('accounting-audit-log_view-logs_hasExpired');
        const canViewAutomations = hasPermission('automation-management_view-automations');
        const canViewAutomationsExpired = hasPermission('automation-management_view-automations_hasExpired');
        const canSnooze = hasPermission('snooze_snooze-devices');
        const { deviceId } = useParams();

        const snoozeRef: any = React.useRef(null);

        useGetUserProfileQuery({}, { skip: !currentRealmName }) as ApiEndpointType<ApiResponseType<Profile>>;
        const { data: featureFlagData } = useGetAllFeatureFlagQuery({});
        const isTosPrivacyFeatureEnabled =
            featureFlagData?.data?.some(
                (flag: FeatureFlagData) =>
                    flag.featureFlagName?.toLowerCase?.() === TOS_PRIVACY_FEATURE_FLAG && flag.enabled
            ) ?? false;
        const { data: userProfile, isLoading } = useGetUserProfileQuery(
            {},
            { skip: !currentRealmName }
        ) as ApiEndpointType<ApiResponseType<Profile>>;
        const { data: eulaAgreementsContent } = useGetRegisteredUserAgreementsPublicQuery(
            {
                realmPrefix: currentRealmName || 'platform',
            },
            { skip: !isTosPrivacyFeatureEnabled }
        ) as any;
        const isEatonAdmin = !isLoading && Boolean(userProfile?.data?.isRootUser);

        const featureFlag = featureFlagData?.data?.find(
            (x: FeatureFlagData) => x.featureFlagName.toLowerCase() === 'channelmanager'
        );
        const [getUserLogoutApiCall] = useGetUserLogoutApiCallMutation();

        const [openUnSnooze, setOpenUnSnooze] = React.useState(false);
        const [isHelpScreenVisible, setIsHelpScreenVisible] = React.useState(false);
        const selectedNode = useTypedSelector((state: any) => state?.common.selectedNode);

        const handleClickAway = (): void => {
            setOpenUnSnooze(false);
        };

        const time: any = useCountdownTimer(deviceId ?? '', true);

        const handleUserLogout = useCallback(async (): Promise<void> => {
            await getUserLogoutApiCall({});
            await oktaAuth.signOut();
            sessionStorage.setItem('logoutReferrer', sessionStorage.getItem('referrer') ?? '');
            if (window.localStorage.getItem('tempLocale')) window.localStorage.removeItem('tempLocale');
        }, []);

        const getMenuItems = (): any => {
            const menuItem: any[] = [
                {
                    title: <CustomTransComponent translationKey={'COMMON:ORG_LABEL'} />,
                    icon: (
                        <Avatar
                            sx={{
                                backgroundColor: theme?.palette?.primary?.[50],
                                color: theme?.palette?.primary?.main,
                            }}
                            alt={currentOrg?.name}
                            src={currentOrg?.logo ?? ''}
                        >
                            {currentOrg?.name && getUserInitials(currentOrg?.name)}
                        </Avatar>
                    ),
                    subtitle: currentOrg?.orgCode,
                    chevron: true,
                    divider: true,
                    onClick: () => navigate(`/${currentRealmName}/organization`),
                },
                ...(canViewGroups || canCreateGroup || canViewGroupsExpired
                    ? [
                          {
                              title: (
                                  <CustomTransComponent
                                      translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.GROUPS_TITLE'}
                                  />
                              ),
                              icon: (
                                  <Avatar sx={{ backgroundColor: theme?.palette?.primary?.main }}>
                                      <AccountTree className="text-white" />
                                  </Avatar>
                              ),
                              subtitle: (
                                  <CustomTransComponent
                                      translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.GROUPS_SUBTITLE'}
                                  />
                              ),
                              chevron: !!canViewGroups,
                              divider: true,
                              className: canViewGroups ? '' : 'disabled',
                              disabled: canViewGroups ? false : canViewGroupsExpired,
                              ...((canViewGroups || canCreateGroup) && {
                                  onClick: (): void => navigate(`/${currentRealmName}/groups`),
                              }),
                          },
                      ]
                    : []),
                {
                    title: (
                        <CustomTransComponent
                            translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.NOTIFICATIONS_TITLE'}
                        />
                    ),
                    icon: (
                        <Avatar
                            sx={{
                                backgroundColor: theme?.palette?.primary?.[50],
                                color: theme?.palette?.primary?.main,
                            }}
                        >
                            <PushNotificationSolid />
                        </Avatar>
                    ),

                    subtitle: (
                        <CustomTransComponent
                            translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.NOTIFICATIONS_SUBTITLE'}
                        />
                    ),
                    chevron: true,
                    divider: true,
                    onClick: () => navigate(`/${currentRealmName}/notifications`),
                },
                ...(canViewAutomations || canCreateAutomation || canViewAutomationsExpired
                    ? [
                          {
                              title: (
                                  <CustomTransComponent
                                      translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.AUTOMATION_TITLE'}
                                  />
                              ) as any,
                              icon: (
                                  <Avatar sx={{ background: Colors.gray[500] }}>
                                      <SmartToy className="gray-50" />
                                  </Avatar>
                              ),
                              subtitle: (
                                  <CustomTransComponent
                                      translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.AUTOMATION_SUBTITLE'}
                                  />
                              ) as any,
                              chevron: !!canViewAutomations,
                              divider: true,
                              className: canViewAutomations ? '' : 'disabled',
                              disabled: canViewAutomations ? false : canViewAutomationsExpired,
                              ...((canViewAutomations || canCreateAutomation) && {
                                  onClick: () => navigate(`/${currentRealmName}/automations`),
                              }),
                          },
                      ]
                    : []),
                ...(canViewUser || canViewRole || canCreateUser || canViewRoleExpired || canViewUserExpired
                    ? [
                          {
                              title: <CustomTransComponent translationKey={'COMMON:USERS'} />,
                              icon: (
                                  <Avatar sx={{ backgroundColor: theme?.palette?.primary?.main }}>
                                      <Group className="text-white" />
                                  </Avatar>
                              ),
                              subtitle: (
                                  <CustomTransComponent
                                      translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.USER_SUBTITLE'}
                                  />
                              ),
                              chevron: !!(canViewUser ?? canViewRole),
                              divider: true,
                              className: canViewUser || canViewRole ? '' : 'disabled',
                              disabled: canViewUser || canViewRole ? false : canViewUserExpired || canViewRoleExpired,
                              ...((canViewUser || canViewRole) && {
                                  onClick: (): void => navigate(`/${currentRealmName}/usersAndRoles`),
                              }),
                          },
                      ]
                    : []),
                ...(canViewLogs || canViewLogsExpired
                    ? [
                          {
                              title: <CustomTransComponent translationKey={'COMMON:AUDIT_LOGS'} />,
                              icon: (
                                  <Avatar
                                      sx={{
                                          backgroundColor: theme?.palette?.primary?.[50],
                                          color: theme?.palette?.primary?.main,
                                      }}
                                  >
                                      <TableChart />
                                  </Avatar>
                              ),
                              subtitle: (
                                  <CustomTransComponent
                                      translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.AUDIT_SUBTITLE'}
                                  />
                              ),
                              chevron: !!canViewLogs,
                              divider: true,
                              className: canViewLogs ? '' : 'disabled',
                              disabled: canViewLogs ? false : canViewLogsExpired,
                              ...(canViewLogs && { onClick: () => navigate(`/${currentRealmName}/auditLogs`) }),
                          },
                      ]
                    : []),
                ...(canViewSystemWidgets ||
                canViewUserWidgets ||
                canViewSystemDashboards ||
                canViewUserDashboards ||
                canViewSystemDashboardsExpired ||
                canViewUserWidgetsExpired ||
                canViewSystemWidgetsExpired
                    ? [
                          {
                              title: (
                                  <CustomTransComponent
                                      translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.DASHBOARD_TITLE'}
                                  />
                              ),
                              icon: (
                                  <Avatar className="bg-gray-200 color-content">
                                      <Dashboard />
                                  </Avatar>
                              ),

                              subtitle: (
                                  <CustomTransComponent
                                      translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.DASHBOARD_SUBTITLE'}
                                  />
                              ),
                              chevron: !!(canViewSystemDashboards ?? canViewUserWidgets ?? canViewSystemWidgets),
                              divider: true,
                              className:
                                  canViewSystemDashboards || canViewUserDashboards || canViewSystemWidgets
                                      ? ''
                                      : 'disabled',
                              disabled:
                                  canViewSystemWidgets || canViewUserWidgets || canViewSystemDashboards
                                      ? false
                                      : canViewSystemDashboardsExpired ?? canViewUserWidgetsExpired,
                              ...((canViewSystemWidgets || canViewUserWidgets || canViewSystemDashboards) && {
                                  onClick: (): void => navigate(`/${currentRealmName}/dashboardWidget`),
                              }),
                          },
                      ]
                    : []),
                ...((featureFlag?.enabled && [
                    {
                        title: (
                            <CustomTransComponent
                                translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.CHANNEL_LIBRARY_TITLE'}
                            />
                        ),
                        icon: (
                            <Avatar
                                sx={{
                                    backgroundColor: theme?.palette?.primary?.[50],
                                    color: theme?.palette?.primary?.main,
                                }}
                            >
                                <DeviceConfiguration />
                            </Avatar>
                        ),

                        subtitle: (
                            <CustomTransComponent
                                translationKey={'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS.CHANNEL_LIBRARY_SUBTITLE'}
                            />
                        ),
                        chevron: true,
                        divider: true,
                        onClick: (): void => navigate(`/${currentRealmName}/channelLibrary`),
                    },
                ]) ||
                    []),
                ...(canViewSystemDashboards ||
                canViewUserDashboards ||
                canViewUserDashboardsExpired ||
                canViewSystemDashboardsExpired
                    ? [
                          {
                              title: (
                                  <CustomTransComponent
                                      size={{ width: '100%' }}
                                      translationKey={
                                          'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS:TARIFFS_AND_CURRENCIES'
                                      }
                                  />
                              ),
                              icon: (
                                  <Avatar
                                      sx={{
                                          backgroundColor: Colors.green[300],
                                          color: Colors.green[50],
                                      }}
                                  >
                                      <MonetizationOn />
                                  </Avatar>
                              ),

                              subtitle: (
                                  <CustomTransComponent
                                      size={{ width: '100%' }}
                                      translationKey={
                                          'COMPONENTS:HEADER_ACTIONS.MENU_ITEM_TEXTS:TARIFFS_AND_CURRENCIES_SUBTITLE'
                                      }
                                  />
                              ),
                              chevron: !!(canViewSystemDashboards ?? canViewUserDashboards),
                              divider: true,
                              className: canViewSystemDashboards || canViewUserDashboards ? '' : 'disabled',
                              disabled:
                                  canViewSystemDashboards || canViewUserDashboards
                                      ? false
                                      : canViewSystemDashboardsExpired ?? canViewUserDashboardsExpired,
                              ...((canViewSystemDashboards || canViewUserDashboards) && {
                                  onClick: (): void => navigate(`/${currentRealmName}/tariffsAndCurrencies`),
                              }),
                          },
                      ]
                    : []),
            ];

            menuItem.push({
                title: '',
                subtitle: `v${process.env.REACT_APP_VERSION ?? ''}.${window.APP_CONFIG.BUILD_NUMBER}`,
                className: 'build-version-text',
            });

            return menuItem;
        };
        const getPrivacyNoticeUrl = (): string | undefined => {
            const privacyNoticeAgreement = eulaAgreementsContent?.data?.agreements?.find(
                (agreement: any) => agreement.type === 'PRIVACY_NOTICE'
            );
            return privacyNoticeAgreement?.url;
        };
        const navigateToPolicyPage = (url: string): void => {
            const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
            if (openedWindow) {
                openedWindow.focus();
            }
        };

        return (
            <>
                {!isSuperRealm && isEatonAdmin && deviceId && <Quarantine />}

                {!isSystemAdmin && !isSuperRealm && !isAdminRealm ? (
                    <UserMenu
                        mr={1}
                        id="need-help"
                        MenuProps={{
                            style: {
                                top: '16px',
                            },
                            sx: {
                                '& .MuiButtonBase-root ': {
                                    padding: '9px 16px',
                                },
                                '& .MuiListItem-root': {
                                    height: 'auto',
                                },
                                '& .BluiInfoListItem-title': {
                                    fontWeight: '600 !important',
                                    lineHeight: '22px',
                                    color: '#424E54',
                                },
                            },
                        }}
                        avatar={
                            <IconButton data-cy={'need-help'} color={'inherit'} size="large">
                                <Help />
                            </IconButton>
                        }
                        menuGroups={[
                            {
                                items: [
                                    {
                                        title: t('bluiRegistration:MESSAGES.CONTACT'),
                                        icon: <Help />,
                                        onClick: (): void => setIsHelpScreenVisible(true),
                                    },
                                    {
                                        title: t('bluiRegistration:MESSAGES.PRIVACY'),
                                        icon: <Policy />,
                                        onClick: (): void => {
                                            if (isTosPrivacyFeatureEnabled) {
                                                const privacyUrl = getPrivacyNoticeUrl();
                                                if (privacyUrl) {
                                                    navigateToPolicyPage(privacyUrl);
                                                    return;
                                                }
                                            }
                                            policyRef.current?.modalHandler?.(true);
                                        },
                                    },
                                ],
                            },
                        ]}
                    />
                ) : (
                    <></>
                )}

                <CreateSnoozeModal ref={snoozeRef} deviceId={deviceId} />

                {deviceId && canEditDevices && canSnooze && window?.location?.pathname?.includes('device') && (
                    <Stack direction={'row'} alignItems={'center'}>
                        <Stack direction={'row'} alignItems={'center'}>
                            {selectedNode?.connectionStatus !== 'Offline' &&
                                (!time?.isVisible ? (
                                    <IconButton
                                        color={'inherit'}
                                        size="large"
                                        id="snooze-button"
                                        onClick={(): void => snoozeRef?.current?.onClick(true)}
                                        disabled={selectedNode?.deviceStatus === 'Quarantined'}
                                    >
                                        <NotificationsPaused />
                                    </IconButton>
                                ) : (
                                    <Stack direction={'row'} alignItems={'center'} mr={2}>
                                        <IconButton
                                            id="unsnooze-button"
                                            color={'inherit'}
                                            size="large"
                                            onClick={(): void => setOpenUnSnooze((prev) => !prev)}
                                            disabled={selectedNode?.deviceStatus === 'Quarantined'}
                                        >
                                            <Snooze />
                                        </IconButton>
                                        <Typography variant="caption" fontSize={'12px'}>
                                            {timerString}
                                        </Typography>

                                        {openUnSnooze && (
                                            <ClickAwayListener onClickAway={handleClickAway}>
                                                <Paper className="drop-down-menu">
                                                    <List disablePadding>
                                                        <ListItemButton
                                                            id="unsnooze"
                                                            onClick={(): void => {
                                                                snoozeRef?.current?.submitHandler(false);
                                                                setOpenUnSnooze(false);
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    <CustomTransComponent
                                                                        translationKey={'COMMON:UNSNOOZE'}
                                                                    />
                                                                }
                                                            />
                                                        </ListItemButton>
                                                    </List>
                                                </Paper>
                                            </ClickAwayListener>
                                        )}
                                    </Stack>
                                ))}
                        </Stack>
                        <Divider orientation="vertical" className="custom-divider" />
                    </Stack>
                )}
                {!isSystemAdmin && !disabled && !isSuperRealm && !isAdminRealm && (
                    <UserMenu
                        className={classes.toolbarRightItem}
                        id="users"
                        MenuProps={{
                            style: {
                                top: '16px',
                            },
                            sx: {
                                '& .MuiButtonBase-root ': {
                                    padding: '9px 16px',
                                    height: '64px !important',
                                },
                                '& .MuiListItem-root': {
                                    height: '64px !important',
                                },
                                '& .BluiInfoListItem-title': {
                                    fontWeight: '600 !important',
                                    lineHeight: '22px',
                                    color: ' #424E54',
                                },
                            },
                        }}
                        avatar={
                            <IconButton color={'inherit'} size="large" data-cy={'settings'}>
                                <Settings />
                            </IconButton>
                        }
                        menuGroups={[
                            {
                                items: getMenuItems(),
                            },
                        ]}
                    />
                )}
                {!isUpcomingServicePortal && (
                    <UserMenu
                        id="profile-menu"
                        MenuProps={{
                            style: {
                                top: '16px',
                                width: '25%',
                            },
                        }}
                        avatar={
                            <Avatar
                                src={userProfile?.data?.image || ''}
                                sx={{
                                    color: theme?.palette?.primary?.main,
                                    backgroundColor: theme?.palette?.primary?.[50],
                                }}
                            >
                                {userProfile?.data?.firstName || userProfile?.data?.lastName ? (
                                    getUserInitials(
                                        `${userProfile?.data?.firstName || ' '} ${userProfile?.data?.lastName || ''}`
                                    )
                                ) : (
                                    <Person fontSize="inherit" />
                                )}
                            </Avatar>
                        }
                        menuGroups={[
                            {
                                items: [
                                    {
                                        title: (<CustomTransComponent translationKey={'COMMON:MY_PROFILE'} />) as any,
                                        icon: <Person />,
                                        onClick: (): void => {
                                            if (isSystemAdmin) {
                                                navigate(`/admin/myProfile`);
                                            } else {
                                                navigate(`/${currentRealmName}/myProfile`);
                                            }
                                        },
                                    },
                                    {
                                        title: (
                                            <CustomTransComponent
                                                translationKey={'COMPONENTS:HEADER_ACTIONS.LOG_OUT_LABEL'}
                                            />
                                        ) as any,
                                        icon: <Input />,
                                        onClick: handleUserLogout as any,
                                    },
                                ],
                            },
                        ]}
                        menuTitle={`${userProfile?.data?.firstName || '-'} ${userProfile?.data?.lastName || '-'}`}
                        menuSubtitle={`${userProfile?.data?.email || '-'}`}
                    />
                )}

                <NeedHelpScreen
                    isOpen={isHelpScreenVisible}
                    handleCloseModal={(): void => setIsHelpScreenVisible(false)}
                />
                <PrivacyPolicy ref={policyRef} />
            </>
        );
    }
);
